<template>
  <div class="dict-table-shell" ref="shell">
    <div
      class="dict-table-head-placeholder"
      :style="headPlaceStyle"
      aria-hidden="true"
    ></div>
    <div
      ref="stickyHead"
      class="dict-table-sticky-head"
      :class="{ 'dict-table-sticky-head--pinned': headPinned }"
      :style="headPinnedStyle"
    >
      <div
        class="dict-table-hscroll"
        ref="headScroll"
        @scroll="syncHeadScroll"
      >
        <table :class="['dict-table', 'dict-table--header', narrowTable ? 'dict-table--narrow' : '']">
          <colgroup>
            <template v-if="narrowTable">
              <col class="dict-table__col dict-table__col--form">
              <col class="dict-table__col dict-table__col--alt">
            </template>
            <template v-else>
              <col class="dict-table__col dict-table__col--disclosure">
              <col class="dict-table__col dict-table__col--form">
              <col class="dict-table__col dict-table__col--alt">
              <col class="dict-table__col dict-table__col--votes">
              <col class="dict-table__col dict-table__col--comment">
              <col class="dict-table__col dict-table__col--details">
            </template>
          </colgroup>
          <thead class="dict-table__head">
            <tr v-if="narrowTable">
              <th class="dict-table__th dict-table__th--form" scope="col">{{ $t('dict.table.form_short') }}</th>
              <th class="dict-table__th dict-table__th--alt" scope="col">{{ $t('dict.table.alt_short') }}</th>
            </tr>
            <tr v-else>
              <th class="dict-table__th dict-table__th--disclosure" scope="col" aria-hidden="true"></th>
              <th class="dict-table__th dict-table__th--form" scope="col">{{ $t('dict.for_label') }}</th>
              <th class="dict-table__th dict-table__th--alt" scope="col">{{ $t('dict.neutral_label') }}</th>
              <th class="dict-table__th dict-table__th--votes" scope="col">
                <span class="sr-only">{{ $t('dict.table.votes') }}</span>
              </th>
              <th class="dict-table__th dict-table__th--comment" scope="col">{{ $t('dict.table.comment') }}</th>
              <th class="dict-table__th dict-table__th--details" scope="col">{{ $t('dict.table.details') }}</th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
    <div class="dict-table-wrap dict-table-wrap--body">
      <div
        class="dict-table-hscroll"
        ref="bodyScroll"
        @scroll="syncBodyScroll"
      >
        <table :class="['dict-table', 'dict-table--body', narrowTable ? 'dict-table--narrow' : '']">
          <colgroup>
            <template v-if="narrowTable">
              <col class="dict-table__col dict-table__col--form">
              <col class="dict-table__col dict-table__col--alt">
            </template>
            <template v-else>
              <col class="dict-table__col dict-table__col--disclosure">
              <col class="dict-table__col dict-table__col--form">
              <col class="dict-table__col dict-table__col--alt">
              <col class="dict-table__col dict-table__col--votes">
              <col class="dict-table__col dict-table__col--comment">
              <col class="dict-table__col dict-table__col--details">
            </template>
          </colgroup>
          <dict-entry-table-group
            v-for="entry in entries"
            :key="entry.id"
            :entry="entry"
            :narrow-table="narrowTable"
            :parent-search-term="parentSearchTerm"
            :alternative-label="alternativeSectionLabel(entry)"
            :shortform-placeholder="shortformPlaceholder"
            :shortform-placeholder-profile="shortformPlaceholderProfile"
            @like="$emit('like', $event)"
            @dislike="$emit('dislike', $event)"
            @add="$emit('add', $event)"
            @flag="$emit('flag', $event)"
            @register="$emit('register')"
          />
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { dictionaryEntryDisplayMixin } from '../mixins/dictionaryEntryDisplayMixin'
import { dictTableLayoutMixin } from '../mixins/dictTableLayoutMixin'
import { syncNavbarHeight } from '@/utils/layoutSync'
import DictEntryTableGroup from './DictEntryTableGroup'

export default {
  name: 'DictEntryTable',
  components: { DictEntryTableGroup },
  mixins: [dictionaryEntryDisplayMixin, dictTableLayoutMixin],
  props: {
    entries: { type: Array, required: true },
    parentSearchTerm: { default: '' }
  },
  data () {
    return {
      headPinned: false,
      headPlaceStyle: {},
      headPinnedStyle: {}
    }
  },
  watch: {
    narrowTable () {
      this.$nextTick(() => this.updatePinnedHead())
    },
    entries () {
      this.$nextTick(() => this.updatePinnedHead())
    }
  },
  mounted () {
    this._onScroll = () => this.updatePinnedHead()
    this._onResize = () => this.updatePinnedHead()
    window.addEventListener('scroll', this._onScroll, { passive: true })
    window.addEventListener('resize', this._onResize)
    this.$nextTick(() => this.updatePinnedHead())
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this._onScroll)
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    navbarOffset () {
      syncNavbarHeight()
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--ga-navbar-offset').trim()
      const value = parseFloat(raw)
      return Number.isFinite(value) ? value : 56
    },
    updatePinnedHead () {
      const stickyHead = this.$refs.stickyHead
      const shell = this.$refs.shell
      if (!stickyHead || !shell || !this.narrowTable) {
        this.headPinned = false
        this.headPlaceStyle = {}
        this.headPinnedStyle = {}
        return
      }
      const offset = this.navbarOffset()
      const shellRect = shell.getBoundingClientRect()
      const headHeight = stickyHead.offsetHeight
      const shouldPin = shellRect.top <= offset && shellRect.bottom > offset + headHeight
      this.headPinned = shouldPin
      if (shouldPin) {
        this.headPinnedStyle = {
          top: `${offset}px`,
          left: `${shellRect.left}px`,
          width: `${shellRect.width}px`
        }
        this.headPlaceStyle = { height: `${headHeight}px` }
      } else {
        this.headPinnedStyle = {}
        this.headPlaceStyle = {}
      }
    },
    syncBodyScroll () {
      if (this._scrollSync) return
      this._scrollSync = true
      const head = this.$refs.headScroll
      const body = this.$refs.bodyScroll
      if (head && body) head.scrollLeft = body.scrollLeft
      this._scrollSync = false
    },
    syncHeadScroll () {
      if (this._scrollSync) return
      this._scrollSync = true
      const head = this.$refs.headScroll
      const body = this.$refs.bodyScroll
      if (head && body) body.scrollLeft = head.scrollLeft
      this._scrollSync = false
    }
  }
}
</script>
